'use client'

import { cn } from "@/lib/utils"

/**
 * The Choice Graphics mark is a typeset Anton lockup, not vector artwork
 * (design spec section 8). The retired script SVG carried the brand as
 * paths; this renders it as two real words so the DOM text is the brand
 * name, the mark inherits the theme scope's colours through
 * --cw-logo-primary / --cw-logo-secondary, and it scales by font-size.
 *
 * DOM text stays mixed case and the shout is applied in CSS, so anything
 * reading the markup (screen reader, crawler, copy-paste) gets
 * "Choice Graphics" rather than shouted capitals.
 */

/**
 * Font sizes calibrated against the retired SVG's rendered box at every
 * call site (task 18 step 2). The old mark was a fixed-height image:
 * sm 30px, md 40px, lg 50px, xl 60px, 2xl 70px. A stacked lockup is two
 * line boxes at leading 1.02, so its height is 2.04x the font size and
 * each value below is size / 2.04, which measures within half a pixel of
 * the height it replaces.
 *
 * The 1.02 leading rides on the size class as a `/[1.02]` modifier rather
 * than a separate `leading-[1.02]` utility on purpose: tailwind-merge puts
 * `leading-*` in the conflict set of `text-{size}`, so a standalone leading
 * class declared before the size class is silently dropped.
 */
const SIZES = {
  sm: "text-[14.7px]/[1.02]",    // 29.99px stacked, replaces 30
  md: "text-[19.6px]/[1.02]",    // 39.98px stacked, replaces 40
  lg: "text-[24.5px]/[1.02]",    // 49.98px stacked, replaces 50
  xl: "text-[29.4px]/[1.02]",    // 59.98px stacked, replaces 60
  "2xl": "text-[34.3px]/[1.02]", // 69.97px stacked, replaces 70
} as const

export type ChoiceLogoSize = keyof typeof SIZES

/**
 * `stacked` is the supplied lockup: second word on its own line. `inline`
 * sets both words on one line for horizontal chrome that cannot afford a
 * two-line mark.
 */
export type ChoiceLogoOrientation = "stacked" | "inline"

export interface ChoiceLogoProps extends React.ComponentPropsWithoutRef<"span"> {
  size?: ChoiceLogoSize
  orientation?: ChoiceLogoOrientation
  classNames?: {
    base?: string
    primary?: string
    secondary?: string
  }
}

export function ChoiceLogo({
  size = "md",
  orientation = "stacked",
  className,
  classNames,
  ...props
}: ChoiceLogoProps) {
  return (
    <span
      data-slot="logo"
      className={cn(
        "[font-family:var(--font-anton),ui-sans-serif,sans-serif] font-normal uppercase tracking-[0.5px] whitespace-nowrap",
        orientation === "stacked"
          ? "inline-flex flex-col"
          : "inline-flex gap-[0.35em]",
        SIZES[size],
        classNames?.base,
        className
      )}
      {...props}
    >
      {/* Colour comes from the surrounding theme scope: both words ink on
          light, paper and lime inside .cw-dark (choice-graphics.css). The
          var() fallback is the light-scope ink value, which only matters
          if this ever renders where neither scope sets the variable. */}
      <span
        data-slot="logo-primary"
        className={cn("[color:var(--cw-logo-primary,#1C3144)]", classNames?.primary)}
      >
        Choice
      </span>
      <span
        data-slot="logo-secondary"
        className={cn("[color:var(--cw-logo-secondary,#1C3144)]", classNames?.secondary)}
      >
        Graphics
      </span>
    </span>
  )
}
